import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, ShieldAlert } from 'lucide-react';
import { useAuth } from '@/features/auth/AuthContext';
import { useLanguage } from '@/hooks/useLanguage';
import { ApiError, api } from '@/lib/api';
import { Button, Card, CardBody, CardHeader, Field, Input, useToast } from '@/components/ui';

export function ChangePasswordPage() {
  const { t } = useLanguage();
  const { user, signOut } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (newPassword !== confirmation) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword });
      toast.success(t('auth.passwordChanged'));
      // Le changement de mot de passe revoque toutes les sessions ouvertes.
      await signOut();
      navigate('/login', { replace: true });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.localized() : t('errors.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-lg flex-col justify-center px-4 py-10">
      <Card>
        <CardHeader
          title={t('auth.changePassword')}
          description={user?.mustChangePassword ? t('auth.mustChangePassword') : undefined}
        />
        <CardBody>
          {user?.mustChangePassword ? (
            <div className="mb-5 flex items-start gap-3 rounded-xl bg-warning-soft px-4 py-3 text-sm text-warning">
              <ShieldAlert className="mt-0.5 h-4.5 w-4.5 shrink-0" aria-hidden />
              <p>{t('auth.mustChangePassword')}</p>
            </div>
          ) : null}

          <form onSubmit={(event) => void onSubmit(event)} className="space-y-4" noValidate>
            <Field label={t('auth.currentPassword')} htmlFor="current" required>
              <Input
                id="current"
                type="password"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
              />
            </Field>

            <Field label={t('auth.newPassword')} htmlFor="next" hint={t('auth.passwordPolicy')} required>
              <Input
                id="next"
                type="password"
                autoComplete="new-password"
                required
                minLength={10}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
            </Field>

            <Field label={t('auth.confirmPassword')} htmlFor="confirm" required>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                required
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                invalid={confirmation.length > 0 && confirmation !== newPassword}
              />
            </Field>

            {error ? (
              <p role="alert" className="rounded-xl bg-danger-soft px-4 py-3 text-sm font-medium text-danger">
                {error}
              </p>
            ) : null}

            <Button type="submit" loading={submitting} icon={<KeyRound className="h-4 w-4" />} className="w-full justify-center">
              {t('auth.changePassword')}
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
